function getRgbFromHex (colorHex: string) {
    const sanitizedHex = colorHex.replace(/^#/, '').toUpperCase()

    if (!(sanitizedHex.length === 3 || sanitizedHex.length === 6)) {
        return {
            r: 0,
            g: 0,
            b: 0
        }
    }
    // Parse the hex color
    let r: number, g: number, b: number
    if (sanitizedHex.length === 3) {
        // Shorthand hex format #RGB
        r = parseInt(sanitizedHex.charAt(0) + sanitizedHex.charAt(0), 16)
        g = parseInt(sanitizedHex.charAt(1) + sanitizedHex.charAt(1), 16)
        b = parseInt(sanitizedHex.charAt(2) + sanitizedHex.charAt(2), 16)
    } else if (sanitizedHex.length === 6) {
        // Full hex format #RRGGBB
        r = parseInt(sanitizedHex.substring(0, 2), 16)
        g = parseInt(sanitizedHex.substring(2, 4), 16)
        b = parseInt(sanitizedHex.substring(4, 6), 16)
    } else {
        throw new Error('Invalid hex color format')
    }

    return {
        r,
        g,
        b
    }
}

function getBrightness (hex: string) {
    const { r, g, b } = getRgbFromHex(hex)

    return (r * 299 + g * 587 + b * 114) / 1000
}

export function isDarkColor (color = '') {
    return getBrightness(color) < 128
}

export function isLightnessFromHex (hex = ''): number {
    const { r, g, b } = getRgbFromHex(hex)

    // Convert RGB to [0, 1] range
    const rNorm = r / 255
    const gNorm = g / 255
    const bNorm = b / 255

    // Determine min and max for lightness calculation
    const max = Math.max(rNorm, gNorm, bNorm)
    const min = Math.min(rNorm, gNorm, bNorm)

    // Lightness is the average of max and min
    const lightness = (max + min) / 2

    // Convert to percentage and return
    return lightness * 100
}

export function adjustColor (hex: string, percent: number) {
    if (!/^#([0-9A-Fa-f]{6})$/.test(hex)) return '#888888' // Validate hex input

    let r = parseInt(hex.slice(1, 3), 16)
    let g = parseInt(hex.slice(3, 5), 16)
    let b = parseInt(hex.slice(5, 7), 16)

    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

    // Handle "almost black" and "almost white" dynamically
    if (luminance <= 0.05 && percent > 0) {
        // Very dark color → Lighten toward neutral gray
        r = g = b = Math.min(255, 20 * Math.abs(percent))
    } else if (luminance >= 0.95 && percent < 0) {
        // Very light color → Darken toward neutral gray
        r = g = b = Math.max(0, 255 - 20 * Math.abs(percent))
    } else {
        // General adjustment for other colors
        r = clamp(Math.round(r + ((percent / 100) * (percent > 0 ? 255 - r : r))), 0, 255)
        g = clamp(Math.round(g + ((percent / 100) * (percent > 0 ? 255 - g : g))), 0, 255)
        b = clamp(Math.round(b + ((percent / 100) * (percent > 0 ? 255 - b : b))), 0, 255)
    }

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}


function clamp (value: number, min: number, max: number) {
    return Math.min(max, Math.max(min, value))
}

function toHex (value: number) {
    return value.toString(16).padStart(2, '0')
}
